import {Component, effect, inject, signal} from '@angular/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {CommonModule} from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {TextInputComponent} from '../../components/form-fields/text-input/text-input.component';
import {OtpInputComponent} from '../../components/form-fields/otp-input/otp-input.component';
import {
  PasswordInputComponent
} from '../../components/form-fields/password-input/password-input.component';
import {Store} from '@ngrx/store';
import {AuthState} from '../../store/auth.reducer';
import {CommonService} from '../../services/common.service';
import {AuthService} from '../../store/auth.service';
import {combineLatest, Subject, takeUntil} from 'rxjs';
import {forgotPasswordEmail, isForgotPasswordLoading, isResetPasswordLoading, isValidatePasswordResetOtpLoading} from '../../store/auth.selectors';
import {map, distinctUntilChanged, filter, debounceTime} from 'rxjs/operators';
import {emailRegex} from '../../constants/validation.constants';
import * as AuthActions from '../../store/auth.actions';
import {
  PrimaryButtonComponent
} from '../../components/buttons/primary-button/primary-button.component';
import {
  OutlinedButtonComponent
} from '../../components/buttons/outlined-button/outlined-button.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-forgot-password-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    TextInputComponent,
    OtpInputComponent,
    PasswordInputComponent,
    PrimaryButtonComponent,
    OutlinedButtonComponent
  ],
  templateUrl: './forgot-password-form.component.html',
  styleUrl: './forgot-password-form.component.css'
})
export class ForgotPasswordFormComponent {
  private store = inject(Store<{ auth: AuthState }>);
  private commonService = inject(CommonService);
  private authService = inject(AuthService);

  emailForm: FormGroup;
  passwordForm: FormGroup;
  isForgotPasswordLoading$ = this.store.select(isForgotPasswordLoading);
  isResetPasswordLoading$ = this.store.select(isResetPasswordLoading);
  isValidatePasswordResetOtpLoading$ = this.store.select(isValidatePasswordResetOtpLoading);
  isLoading$ = combineLatest([this.isForgotPasswordLoading$, this.isResetPasswordLoading$, this.isValidatePasswordResetOtpLoading$]).pipe(
    map(([isForgotPasswordLoading, isResetPasswordLoading, isValidatePasswordResetOtpLoading]) => 
      isForgotPasswordLoading || isResetPasswordLoading || isValidatePasswordResetOtpLoading
    )
  );
  email$ = this.store.select(forgotPasswordEmail);

  tokenExists = this.commonService.getForgotPasswordSuccess();
  timeLeft = this.commonService.timeLeftSignal;
  lastEmail: string = '';
  showPasswordForm: boolean = false;
  isOtpValidated = signal(false);
  private isOtpValidationInProgress = false;

  errorMessages = {
    email: '',
    otp: '',
    password: '',
    confirmPassword: ''
  }

  errorMessageMap = {
    email: {
      required: 'Email is required.',
      pattern: 'Please enter a valid email address.'
    },
    otp: {
      required: 'OTP is required.',
      minlength: 'OTP must be 6 digits.',
      maxlength: 'OTP must be 6 digits.',
      pattern: 'OTP must contain numbers only.'
    },
    password: {
      required: 'Password is required.',
      minlength: 'Password must be at least 6 characters.',
      maxlength: 'Password cannot exceed 20 characters.',
      lowerCaseCheckFailed: 'Include at least 1 lowercase letter.',
      upperCaseCheckFailed: 'Include at least 1 uppercase letter.',
      numberCheckFailed: 'Include at least 1 number.',
      specialCharCheckFailed: 'Include at least 1 special character (e.g., !@#$%).',
      containsSpaceCheckFailed: 'Password cannot contain spaces.'
    },
    confirmPassword: {
      required: 'Please re-enter your password.',
      passwordMatchFailed: 'Please ensure both passwords match'
    }
  }

  constructor(private fb: FormBuilder) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
    });

    this.email$.pipe(takeUntilDestroyed())
      .subscribe((email) => {
        if (email) {
          this.emailForm.patchValue({email});
          // Update lastEmail when email is set from store
          this.lastEmail = email;
        }
      })


    this.passwordForm = this.fb.group({
      otp: new FormControl({value: '', disabled: false}, [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('^[0-9]*$')
      ]),
      password: new FormControl({value: '', disabled: false}, [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(20),
          this.commonService.validatePassword
        ],
      ),
      confirmPassword: new FormControl({value: '', disabled: false}, [
          Validators.required,
        ]
      )
    }, {
      validators: [this.validateConfirmPassword]
    })

    // Start timer when token exists becomes true
    effect(() => {
      if (this.tokenExists()) {
        this.commonService.startTimer();
        this.emailForm.controls['email'].disable();

      }
    });

    // Subscribe to OTP input valueChanges
    this.passwordForm.controls['otp'].valueChanges
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        debounceTime(100), // Small debounce to handle rapid value changes
        filter((value) => {
          // Only process when value is exactly 6 digits, token exists, not validated, and not already in progress
          return value?.length === 6 && 
                 this.tokenExists() && 
                 !this.isOtpValidated() && 
                 !this.isOtpValidationInProgress;
        })
      )
      .subscribe((value) => {
        // Clear OTP error when user starts typing (though filtered above, keep for safety)
        if (value && value.length < 6) {
          this.errorMessages = {
            ...this.errorMessages,
            otp: ''
          };
          return;
        }

        // When OTP length is 6, dispatch validatePasswordResetOtp
        // Get email - prefer lastEmail (set when forgotPassword was dispatched), then form value
        const email = this.lastEmail || this.emailForm.get('email')?.value;
        if (email) {
          this.isOtpValidationInProgress = true;
          this.store.dispatch(AuthActions.validatePasswordResetOtp({
            email,
            otp: value
          }));
        }
      });

    // Subscribe to passwordResetOtpValidationResult Subject
    this.authService.passwordResetOtpValidationResult
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        // Reset validation in progress flag
        this.isOtpValidationInProgress = false;

        if (result.success) {
          // On success: set signal to show password fields
          this.isOtpValidated.set(true);
          // Clear errors and reset password fields
          setTimeout(() => {
            this.passwordForm.controls['password'].setErrors(null);
            this.passwordForm.controls['confirmPassword'].setErrors(null);
            this.errorMessages = {
              ...this.errorMessages,
              otp: '',
              password: '',
              confirmPassword: ''
            };
          });
        } else {
          // On failure: keep OTP screen visible and set OTP error message
          this.isOtpValidated.set(false);
          this.errorMessages = {
            ...this.errorMessages,
            otp: result.error || 'Invalid OTP. Please try again.'
          };
          // Reset OTP field
          this.passwordForm.controls['otp'].reset();
        }
      })
  }

  ngOnInit() {
    this.commonService.removeItemFromLocalStorage('neuralops/reset-password-token');
    this.commonService.setForgotPasswordSuccess(false);


  }

  validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const passwordControl = control.get('password');
    const confirmPasswordControl = control.get('confirmPassword');

    if (passwordControl && confirmPasswordControl && passwordControl.value !== confirmPasswordControl.value) {
      confirmPasswordControl.setErrors({passwordMatchFailed: true});
    } else if (confirmPasswordControl?.hasError('passwordMatchFailed')) {
      confirmPasswordControl.setErrors(null);
    }

    return null;
  }

  checkForEmailFormErrors(currentField?: any) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.emailForm, this.errorMessageMap, currentField))
    };
  }

  checkForPasswordFormErrors(currentField?: any) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.passwordForm, this.errorMessageMap, currentField))
    };
  }

  passwordMatchValidator(control: FormControl): { [key: string]: boolean } | null {
    const password = this.emailForm?.get('password')?.value;
    const confirmPassword = control.value;

    if (password !== confirmPassword) {
      return {'passwordMismatch': true};
    }

    return null;
  }

  submit(event?: Event) {
    if (event) {
      event.preventDefault();
    }


    if (this.tokenExists()) {
      if (!this.passwordForm.valid) {
        this.errorMessages = {
          ...this.errorMessages,
          ...(this.commonService.checkFormValidation(this.passwordForm, this.errorMessageMap))
        };
        return;
      }

      const {otp, password, confirmPassword} = this.passwordForm.getRawValue();

      if (otp && password && confirmPassword) {
        this.store.dispatch(AuthActions.resetPassword({
          otp,
          password,
          confirmPassword
        }));
      }
    } else {
      if (!this.emailForm.valid) {
        this.checkForEmailFormErrors();
        return;
      }

      const email = this.emailForm.get('email')?.value;

      this.lastEmail = email;
      this.store.dispatch(AuthActions.forgotPassword({email}));
    }
  }

  resendOtp(): void {
    this.errorMessages = {
      email: '',
      otp: '',
      password: '',
      confirmPassword: ''
    }

    // Reset OTP validation state and flag
    this.isOtpValidated.set(false);
    this.isOtpValidationInProgress = false;

    if (this.lastEmail) {
      this.commonService.resetTimer();
      this.commonService.startTimer();
      this.passwordForm.reset();
      this.store.dispatch(AuthActions.forgotPassword({email: this.lastEmail}));
    }
  }

  reset() {
    this.commonService.resetTimer();
    this.commonService.removeItemFromLocalStorage('accesshub/reset-password-token');
    this.commonService.setForgotPasswordSuccess(false);
    this.isOtpValidated.set(false);
    this.isOtpValidationInProgress = false;
    this.passwordForm.reset();
    this.emailForm.reset();
    this.emailForm.enable();
  }

  ngOnDestroy(): void {
    this.commonService.stopTimer();
  }
}
